import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Spin, Alert, Typography, Space, Tag } from 'antd';
import { ArrowLeftOutlined, PaperClipOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { useTransactionDetails } from '../hooks/useTransactionManagement';

const { Title } = Typography;

const TransactionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslationWithNamespace('transactions');
  const { t: commonT } = useTranslationWithNamespace('common');
  
  const { data, isLoading, isError, error, refetch } = useTransactionDetails(id || '');

  // Function to map transaction status to a tag
  const mapTransactionStatus = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      Pending: { color: 'processing', text: t('status.pending', 'Pending') },
      Confirmed: { color: 'success', text: t('status.confirmed', 'Confirmed') },
      Failed: { color: 'error', text: t('status.failed', 'Failed') },
      Requested: { color: 'warning', text: t('status.requested', 'Requested') },
      Processing: { color: 'processing', text: t('status.processing', 'Processing') },
      Completed: { color: 'success', text: t('status.completed', 'Completed') },
      Cancelled: { color: 'default', text: t('status.cancelled', 'Cancelled') },
    };

    const defaultStatus = { color: 'default', text: status };
    const mappedStatus = statusMap[status] || defaultStatus;

    return <Tag color={mappedStatus.color}>{mappedStatus.text}</Tag>;
  };

  // Function to handle going back
  const handleGoBack = () => {
    navigate('/transactions');
  };

  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
              {commonT('actions.back', 'Back')}
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {t('transaction.detailsTitle', 'Transaction Details')}
            </Title>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            {commonT('actions.refresh', 'Refresh')}
          </Button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('transaction.loadingDetails', 'Loading transaction details...')}
            </div>
          </div>
        ) : isError ? (
          <Alert
            message={t('transaction.error', 'Error')}
            description={error instanceof Error ? error.message : 'Failed to load transaction details'}
            type="error"
            showIcon
          />
        ) : data?.data ? (
          <Card>
            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label={t('transaction.id', 'ID')}>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {data.data._id || '-'}
                </span>
                <Button
                  type="link"
                  icon={<PaperClipOutlined />}
                  onClick={() => copyToClipboard(data.data._id)}
                  style={{ marginLeft: 8 }}
                >
                  {commonT('actions.copy', 'Copy')}
                </Button>
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.txid', 'Transaction ID')}>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {data.data.txid || '-'}
                </span>
                {data.data.txid && (
                  <Button
                    type="link"
                    icon={<PaperClipOutlined />}
                    onClick={() => copyToClipboard(data.data.txid)}
                    style={{ marginLeft: 8 }}
                  >
                    {commonT('actions.copy', 'Copy')}
                  </Button>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.type', 'Type')}>
                {data.data.type || (data.data.side === 'in' ? 'Deposit' : 'Withdrawal') || '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.status', 'Status')}>
                {mapTransactionStatus(data.data.status)}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.amount', 'Amount')}>
                {data.data.amount || '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.coin', 'Coin')}>
                {data.data.coin_id?.symbol || data.data.coin_symbol || '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.network', 'Network')}>
                {data.data.network_id?.title || data.data.network_title || '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('transaction.date', 'Date')}>
                {data.data.created_at ? new Date(data.data.created_at).toLocaleString() : '-'}
              </Descriptions.Item>
              
              {data.data.from_wallet_id && (
                <Descriptions.Item label={t('transaction.fromWallet', 'From Wallet')}>
                  <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {typeof data.data.from_wallet_id === 'object' 
                      ? data.data.from_wallet_id._id 
                      : data.data.from_wallet_id}
                  </span>
                </Descriptions.Item>
              )}
              
              {data.data.source_address && (
                <Descriptions.Item label={t('transaction.sourceAddress', 'Source Address')}>
                  <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {data.data.source_address}
                  </span>
                  <Button
                    type="link"
                    icon={<PaperClipOutlined />}
                    onClick={() => copyToClipboard(data.data.source_address)}
                    style={{ marginLeft: 8 }}
                  >
                    {commonT('actions.copy', 'Copy')}
                  </Button>
                </Descriptions.Item>
              )}
              
              {data.data.to_address && (
                <Descriptions.Item label={t('transaction.destinationAddress', 'Destination Address')}>
                  <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {data.data.to_address}
                  </span>
                  <Button
                    type="link"
                    icon={<PaperClipOutlined />}
                    onClick={() => copyToClipboard(data.data.to_address)}
                    style={{ marginLeft: 8 }}
                  >
                    {commonT('actions.copy', 'Copy')}
                  </Button>
                </Descriptions.Item>
              )}
              
              {data.data.confirmations !== undefined && (
                <Descriptions.Item label={t('transaction.confirmations', 'Confirmations')}>
                  {data.data.confirmations}
                </Descriptions.Item>
              )}
              
              {data.data.fee !== undefined && (
                <Descriptions.Item label={t('transaction.fee', 'Fee')}>
                  {data.data.fee}
                </Descriptions.Item>
              )}
              
              {data.data.block_number !== undefined && (
                <Descriptions.Item label={t('transaction.blockNumber', 'Block Number')}>
                  {data.data.block_number}
                </Descriptions.Item>
              )}
              
              {data.data.memo && (
                <Descriptions.Item label={t('transaction.memo', 'Memo')}>
                  {data.data.memo}
                </Descriptions.Item>
              )}
              
              {data.data.comment_tag && (
                <Descriptions.Item label={t('transaction.tag', 'Tag')}>
                  {data.data.comment_tag}
                </Descriptions.Item>
              )}
              
              {data.data.user_id && (
                <Descriptions.Item label={t('transaction.userId', 'User ID')}>
                  {data.data.user_id}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        ) : (
          <Alert
            message={t('transaction.notFound', 'Transaction Not Found')}
            description={t('transaction.notFoundDescription', 'The requested transaction could not be found.')}
            type="warning"
            showIcon
          />
        )}
      </Space>
    </div>
  );
};

export default TransactionDetailsPage;
